// What a website review has collected, and what everything on it is called,
// decided once.
//
// Four surfaces render the same rows — the embed widget on the client's site,
// the admin queue, the client's queue, and the share link — and each worked out
// the answers for itself. Where they disagreed, they disagreed about the things
// a person says out loud on a call:
//
//   * **The number on a comment.** The widget numbers pins by position in the
//     page's comments oldest-first. `ReviewCommentList` numbered by position in
//     a list sorted newest-first *and already filtered by the status tab*. On
//     the one review with real feedback that inverts the pair exactly: the
//     widget's #1 ("We talked about this in our meeting…") is the portal's #2.
//     Worse, the portal's number moved when the admin switched from Open to
//     All, so it was never an identifier at all. `commentNumbers()` is the one
//     rule, and it matches the widget: per page, oldest first, over every
//     comment regardless of what is on screen.
//
//   * **A page's name.** `AdminWebsiteReviews` inlined `new URL(url).pathname`
//     for its page chips, which for a site root is the string "/" and reads as
//     a rendering glitch; `ReviewCommentList` had its own `pathOf` that turned
//     the same row into "Home". The single `review_pages` row in the database
//     is a site root, so the two views of it disagreed on screen at once.
//
//   * **How many comments are open.** The admin's list badge only ever had an
//     answer for the review that happened to be selected — `openCount()`
//     returned null for every other row — so the queue could not tell you which
//     site was waiting on you until you clicked into each one.
//
//   * **What state a review is in.** `website_reviews.status` holds only
//     `active` and `archived`. Whether anyone has commented, whether the studio
//     has worked through it, and whether the widget is still accepting anything
//     are all evidence, in the same way `bidStage()` and `strategyStage()` are.

import { PROGRAMS, PROGRAM_SCOPES, getProgram } from '../constants/programs'

/**
 * Every status `review_comments.status` may hold.
 *
 * The check constraint in 0017 allows exactly these four, so this list is the
 * whole vocabulary and a status outside it cannot exist in the column.
 */
export const REVIEW_COMMENT_STATUSES = [
  { key: 'open', label: 'Open', color: '#D13833', bg: '#FEF2F2', done: false },
  { key: 'in_progress', label: 'In progress', color: '#D97706', bg: '#FFFBEB', done: false },
  { key: 'resolved', label: 'Resolved', color: '#059669', bg: '#ECFDF5', done: true },
  { key: 'wont_fix', label: "Won't fix", color: '#6B7280', bg: '#F9FAFB', done: true },
]

/** Status metadata, falling back to `open` — the column default. */
export function commentStatusMeta(status) {
  return REVIEW_COMMENT_STATUSES.find(s => s.key === status) || REVIEW_COMMENT_STATUSES[0]
}

export function isCommentDone(comment) {
  return commentStatusMeta(comment?.status).done
}

export function isCommentOpen(comment) {
  return comment?.status === 'open'
}

const time = (row) => {
  const t = Date.parse(row?.created_at || '')
  return Number.isNaN(t) ? 0 : t
}

/**
 * The number a comment is known by, keyed by comment id.
 *
 * Ordinals run per page, oldest first, across every comment on that page —
 * including resolved ones, because the widget draws a pin for those too and a
 * number that renumbers when something is resolved is not a name for anything.
 * `created_at` ties break on id so two comments saved in the same millisecond
 * still get a stable order rather than whatever the array happened to be in.
 */
export function commentNumbers(comments = []) {
  const byPage = new Map()
  for (const c of comments || []) {
    if (!c || !c.id) continue
    // A reply is part of its thread, not a pin of its own. Numbering it would
    // shift every later pin on the page the moment the studio answered one.
    if (c.parent_id) continue
    const key = c.page_id || 'unassigned'
    if (!byPage.has(key)) byPage.set(key, [])
    byPage.get(key).push(c)
  }
  const numbers = new Map()
  for (const rows of byPage.values()) {
    rows
      .slice()
      .sort((a, b) => time(a) - time(b) || String(a.id).localeCompare(String(b.id)))
      .forEach((c, i) => numbers.set(c.id, i + 1))
  }
  return numbers
}

/**
 * What to call a page.
 *
 * The label the widget reports is preferred; the path is the fallback; a site
 * root is "Home" rather than a bare slash. A comment whose page row is not
 * loaded is "Unassigned" — it belongs somewhere, we just cannot say where.
 */
export function pageLabel(page) {
  const label = typeof page?.label === 'string' ? page.label.trim() : ''
  if (label) return label
  if (!page?.url) return 'Unassigned'
  try {
    const path = new URL(page.url).pathname.replace(/\/+$/, '')
    return path || 'Home'
  } catch {
    return page.url
  }
}

/** How a set of comments splits across the tabs the inbox offers. */
export function reviewCounts(comments = []) {
  const rows = comments || []
  return {
    all: rows.length,
    open: rows.filter(isCommentOpen).length,
    in_progress: rows.filter(c => c?.status === 'in_progress').length,
    done: rows.filter(isCommentDone).length,
  }
}

/**
 * The same split, per review, from one flat fetch of comments.
 *
 * The admin list needs a count for every row it draws, not just the open one,
 * and a per-review query per card is a request storm for an answer that fits in
 * a single select.
 */
export function countsByReview(comments = []) {
  const grouped = new Map()
  for (const c of comments || []) {
    if (!c?.review_id) continue
    if (!grouped.has(c.review_id)) grouped.set(c.review_id, [])
    grouped.get(c.review_id).push(c)
  }
  const out = new Map()
  for (const [reviewId, rows] of grouped) {
    const counts = reviewCounts(rows)
    const newest = rows.reduce((max, c) => Math.max(max, time(c)), 0)
    out.set(reviewId, { ...counts, lastCommentAt: newest ? new Date(newest).toISOString() : null })
  }
  return out
}

export const REVIEW_STAGES = {
  closed: { key: 'closed', label: 'Closed', tone: 'muted' },
  paused: { key: 'paused', label: 'Read only', tone: 'muted' },
  waiting: { key: 'waiting', label: 'Awaiting feedback', tone: 'quiet' },
  open: { key: 'open', label: 'Open comments', tone: 'urgent' },
  clear: { key: 'clear', label: 'All addressed', tone: 'good' },
}

/**
 * Where a review has got to, from the review row and its comments.
 *
 * `waiting` is deliberately not called "not installed": a page row only appears
 * when someone comments, so an empty review and a review whose snippet was
 * never pasted look identical from here. Claiming to know which would be a
 * guess presented as a fact.
 */
export function reviewStageFromCounts(review, counts) {
  if (review?.status === 'archived') return REVIEW_STAGES.closed
  const open = counts?.open || 0
  const all = counts?.all || 0
  if (open > 0) return REVIEW_STAGES.open
  if (review && review.public_commenting === false) return REVIEW_STAGES.paused
  if (all === 0) return REVIEW_STAGES.waiting
  return REVIEW_STAGES.clear
}

export function reviewStage(review, comments = []) {
  return reviewStageFromCounts(review, reviewCounts(comments))
}

/**
 * The program this review's work belongs to, read off the project it hangs
 * off — not inferred from that project's stage.
 *
 * Null when there is no project or it is in no program — the caller
 * shows nothing rather than a step it cannot justify, per `getProgram`.
 */
export function reviewPhase(review) {
  return getProgram(review?.projects)
}

/** Marking up a live site is Website & Growth work; that is what the program sells. */
export const REVIEW_PROGRAM = 'website-growth'

export const REVIEW_PHASE_META =
  PROGRAMS.find(p => p.key === REVIEW_PROGRAM) || null

/**
 * Feedback spread against the page count Website Design is sold with.
 *
 * Website Design is quoted with copywriting "for up to 10 core pages". A review
 * collecting notes on more distinct URLs than that is not proof of scope creep
 * — a reviewer can comment on a page nobody built — but it is the moment to
 * look, and nothing else in the portal would ever raise it.
 */
export function pageScope(pageCount = 0) {
  const scope = PROGRAM_SCOPES[REVIEW_PROGRAM]?.websitePages || null
  const pages = Number(pageCount) || 0
  return { pages, scope, over: scope !== null && pages > scope }
}

/** The embed snippet, built from one place so the docs and the UI agree. */
export function embedSnippet(origin, token) {
  if (!origin || !token) return ''
  return `<script src="${origin}/review-widget.js" data-review-token="${token}" defer></script>`
}

/** The share link a reviewer without a portal login follows. */
export function shareUrl(origin, token) {
  if (!origin || !token) return ''
  return `${origin}/wr/${token}`
}

/**
 * The origin a browser will actually report for a URL.
 *
 * People type "example.com" when asked for an origin and the browser then sends
 * "https://example.com". An exact string match on the un-schemed form fails
 * with a 403 that looks, from the client's side, exactly like the overlay not
 * existing — so normalising on the way in is what keeps that from being silent.
 */
export function normalizeOrigin(value) {
  const raw = String(value == null ? '' : value).trim().replace(/\/+$/, '')
  if (!raw) return ''
  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
  try {
    return new URL(withScheme).origin
  } catch {
    return ''
  }
}
